import * as React from "react";
import { useState, useEffect } from "react";
import { Formik, Form, Field, useField } from "formik";
import styled, { css } from "styled-components";
import URI from "urijs";
import i18n from "i18n";
import { Trans, useTranslation } from "react-i18next";
import { SAMLConfig } from "@graylog/enterprise-api";
import { validateField } from "util/FormsUtils";
import {
  Wizard,
  FormikFormGroup,
  InputOptionalInfo as Opt,
  Spinner,
  Select,
  MultiSelect,
  TimezoneSelect
} from "components/common";
import { Row, Col, ButtonToolbar, Button, Input, Alert } from "components/bootstrap";
import {
  AUTH_SERVICE_TYPE,
  SSO_SAML_PATH,
  SSO_SAML_ACS_PATH,
  SSO_SAML_LOGOUT_PATH
} from "authentication/components/saml/constants";
import formatAuthBackendValidationErrors from "authentication/helpers/formatAuthBackendValidationErrors";
import samlAuthBackendSubmit from "authentication/components/saml/config/helpers/samlAuthBackendSubmit";
import AuthBackendSubmitError from "authentication/components/AuthBackendSubmitError";
import Routes from "routing/Routes";
import useHistory from "routing/useHistory";
import useRoles from "common/hooks/useRoles";
import AppConfig from "util/AppConfig";
import SAMLPEMField from "authentication/components/saml/config/SAMLPEMField";
import useSAMLDefaultAttributeMapping from "authentication/components/saml/hooks/useSAMLDefaultAttributeMapping";
import SAMLCertInfoTable from "authentication/components/saml/config/SAMLCertInfoTable";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
const DEFAULT_SP_BASE_URL = `${window.location.origin}${AppConfig.gl2AppPathPrefix()}`;
const READER_ROLE_NAME = "Reader";
const normalizeURI = (url) => new URI(url).normalizePath().toString();
const StyledDefList = styled.dl(
  ({ theme }) => css`
    dd {
      margin-bottom: ${theme.spacings.sm};
    }
  `
);
const StyledSectionWrapper = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.sm};

    p {
      margin-top: ${theme.spacings.sm};
    }
  `
);
const AttributeMapping = ({ title, mappings }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("strong", null, title), /* @__PURE__ */ React.createElement("ul", null, (mappings != null ? mappings : []).sort().map((attributeName) => /* @__PURE__ */ React.createElement("li", { key: attributeName }, /* @__PURE__ */ React.createElement("code", null, attributeName)))));
const FormSection = ({ title, children = void 0 }) => /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement("h3", null, title), children)));
const BackendFetchError = ({ message, title }) => {
  if (!message) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", style: { wordBreak: "break-word" }, title }, message)));
};
const CertificateInfo = ({ certInfo, error = void 0 }) => {
  if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, error);
  }
  if (!certInfo) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    SAMLCertInfoTable,
    {
      certificate: certInfo.certificate,
      subject: certInfo.subject,
      issuer: certInfo.issuer,
      sha256Fingerprint: certInfo.sha256_fingerprint,
      sha1Fingerprint: certInfo.sha1_fingerprint,
      validFrom: certInfo.valid_from,
      validTo: certInfo.valid_to
    }
  );
};
const CertificateInfoRow = ({ certInfo, error = void 0 }) => /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(CertificateInfo, { certInfo, error })));
const GenerateKeyPairButton = ({ values, onClick }) => {
  const { t } = useTranslation("enterprise_authentication");
  const { spCredentialCertificate, spCredentialPrivateKey } = values;
  const isDestructive = spCredentialCertificate || (spCredentialPrivateKey == null ? void 0 : spCredentialPrivateKey.is_set) || (spCredentialPrivateKey == null ? void 0 : spCredentialPrivateKey.set_value);
  return /* @__PURE__ */ React.createElement(Button, { type: "button", bsStyle: isDestructive ? "danger" : "info", onClick }, isDestructive ? t("saml.saml_backend_config_wizard.regenerate_certificate_and_key") : t("saml.saml_backend_config_wizard.generate_certificate_and_key"));
};
const ValidationErrorSummary = ({ errors }) => {
  const { t } = useTranslation("enterprise_authentication");
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { title: t("saml.saml_backend_config_wizard.validation_errors_title"), bsStyle: "danger" }, t("saml.saml_backend_config_wizard.validation_errors_text"))));
};
const MetadataRefreshInfo = ({ show }) => {
  const { t } = useTranslation("enterprise_authentication");
  if (!show) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", style: { margin: 0 } }, t("saml.saml_backend_config_wizard.metadata_refresh_info"));
};
const fetchCertInfo = (cert, setValue, setError) => {
  if (cert) {
    SAMLConfig.getCertificateInfo({ certificate: cert }).then((info) => {
      setValue(info);
      setError(void 0);
    }).catch((error) => {
      var _a, _b;
      setValue(void 0);
      setError(
        ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.message) || i18n.t("saml.saml_backend_config_wizard.cert_info_fetch_error", { ns: "enterprise_authentication" })
      );
    });
  } else {
    setValue(void 0);
    setError(void 0);
  }
};
const IdPConfigForm = ({
  values,
  roles,
  isSubmitting,
  isFetchingMetadata,
  validationErrors,
  submitErrors,
  metadataFetchErrors,
  spCredentialsGenerateErrors,
  setActiveStep,
  setFieldValue,
  handleMetadataFetch,
  handleSpCredentialFetch,
  defaultAttributeMapping
}) => {
  var _a, _b;
  const { t } = useTranslation("enterprise_authentication");
  const sendTelemetry = useSendTelemetry("saml-idp-configuration-step");
  useEffect(() => {
    fetchCertInfo(
      values.idpCertificate,
      (info) => setFieldValue("idpCertificateInfo", info),
      (error) => setFieldValue("idpCertificateInfoError", error)
    );
  }, [values.idpCertificate, setFieldValue]);
  useEffect(() => {
    fetchCertInfo(
      values.spCredentialCertificate,
      (info) => setFieldValue("spCredentialCertificateInfo", info),
      (error) => setFieldValue("spCredentialCertificateInfoError", error)
    );
  }, [values.spCredentialCertificate, setFieldValue]);
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id }));
  const { idpMetadataRefreshAtLogin: isRefreshAtLogin } = values;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "title",
      label: t("saml.saml_backend_config_wizard.title_label"),
      placeholder: t("saml.saml_backend_config_wizard.title_label"),
      disabled: isSubmitting,
      validate: validateField({ required: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      type: "textarea",
      name: "description",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("saml.saml_backend_config_wizard.description"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: t("saml.saml_backend_config_wizard.description"),
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(FormSection, { title: t("saml.saml_backend_config_wizard.identity_provider_config") }), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpMetadataUrl",
      label: t("saml.saml_backend_config_wizard.metadata_url"),
      placeholder: t("saml.saml_backend_config_wizard.metadata_url"),
      help: t("saml.saml_backend_config_wizard.metadata_url_help"),
      disabled: isSubmitting || isFetchingMetadata,
      validate: validateField({ url: true }),
      buttonAfter: /* @__PURE__ */ React.createElement(
        Button,
        {
          type: "button",
          bsStyle: values.idpMetadataUrl ? "info" : "default",
          disabled: !values.idpMetadataUrl,
          onClick: handleMetadataFetch(values, setFieldValue)
        },
        t("saml.saml_backend_config_wizard.fetch")
      )
    }
  ), /* @__PURE__ */ React.createElement(
    BackendFetchError,
    {
      title: t("saml.saml_backend_config_wizard.fetch_metadata_error_title"),
      message: metadataFetchErrors
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      label: t("saml.saml_backend_config_wizard.metadata_refresh_at_login"),
      id: "idp-metadata-refresh-at-login-checkbox",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        type: "checkbox",
        name: "idpMetadataRefreshAtLogin",
        label: "",
        help: t("saml.saml_backend_config_wizard.metadata_refresh_at_login_help"),
        wrapperClassName: "col-sm-12"
      }
    ),
    /* @__PURE__ */ React.createElement(MetadataRefreshInfo, { show: isRefreshAtLogin })
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpSsoServiceRedirectBindingUrl",
      label: t("saml.saml_backend_config_wizard.sso_redirect_url"),
      placeholder: t("saml.saml_backend_config_wizard.sso_redirect_placeholder"),
      help: t("saml.saml_backend_config_wizard.sso_redirect_help"),
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpSsoServicePostBindingUrl",
      label: t("saml.saml_backend_config_wizard.sso_post_url"),
      placeholder: t("saml.saml_backend_config_wizard.sso_post_placeholder"),
      help: t("saml.saml_backend_config_wizard.sso_post_help"),
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpLogoutServiceRedirectBindingUrl",
      label: t("saml.saml_backend_config_wizard.logout_redirect_url"),
      placeholder: t("saml.saml_backend_config_wizard.logout_redirect_placeholder"),
      help: t("saml.saml_backend_config_wizard.logout_redirect_help"),
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpLogoutServicePostBindingUrl",
      label: t("saml.saml_backend_config_wizard.logout_post_url"),
      placeholder: t("saml.saml_backend_config_wizard.logout_post_placeholder"),
      help: t("saml.saml_backend_config_wizard.logout_post_help"),
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpEntityId",
      label: t("saml.saml_backend_config_wizard.entity_id"),
      placeholder: t("saml.saml_backend_config_wizard.entity_id"),
      help: t("saml.saml_backend_config_wizard.idp_entity_id_help"),
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ required: true })
    }
  ), /* @__PURE__ */ React.createElement(
    SAMLPEMField,
    {
      name: "idpCertificate",
      filename: "idpCertificateFilename",
      label: t("saml.saml_backend_config_wizard.certificate"),
      help: t("saml.saml_backend_config_wizard.idp_certificate_help"),
      isValueSet: !!values.idpCertificate,
      disabled: isRefreshAtLogin
    }
  ), /* @__PURE__ */ React.createElement(CertificateInfoRow, { certInfo: values.idpCertificateInfo, error: values.idpCertificateInfoError }), /* @__PURE__ */ React.createElement(FormSection, { title: t("saml.saml_backend_config_wizard.service_provider_config") }), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "spBaseUrl",
      label: t("saml.saml_backend_config_wizard.base_url"),
      placeholder: t("saml.saml_backend_config_wizard.base_url"),
      help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "saml.saml_backend_config_wizard.base_url_help",
          components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
        }
      )),
      disabled: isSubmitting,
      validate: validateField({ required: true, url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "spEntityId",
      label: t("saml.saml_backend_config_wizard.entity_id"),
      placeholder: t("saml.saml_backend_config_wizard.entity_id"),
      help: t("saml.saml_backend_config_wizard.sp_entity_id_help"),
      disabled: isSubmitting,
      validate: validateField({ required: true })
    }
  ), /* @__PURE__ */ React.createElement(
    SAMLPEMField,
    {
      name: "spCredentialCertificate",
      filename: "spCredentialCertificateFilename",
      label: t("saml.saml_backend_config_wizard.certificate"),
      help: t("saml.saml_backend_config_wizard.sp_certificate_help"),
      isValueSet: !!values.spCredentialCertificate
    },
    t("saml.saml_backend_config_wizard.pem_generate_hint")
  ), /* @__PURE__ */ React.createElement(
    CertificateInfoRow,
    {
      certInfo: values.spCredentialCertificateInfo,
      error: values.spCredentialCertificateInfoError
    }
  ), /* @__PURE__ */ React.createElement(
    SAMLPEMField,
    {
      name: "spCredentialPrivateKey",
      filename: "spCredentialPrivateKeyFilename",
      label: t("saml.saml_backend_config_wizard.private_key"),
      help: t("saml.saml_backend_config_wizard.private_key_help"),
      isValueSet: ((_a = values.spCredentialPrivateKey) == null ? void 0 : _a.is_set) || ((_b = values.spCredentialPrivateKey) == null ? void 0 : _b.set_value),
      encrypted: true
    },
    t("saml.saml_backend_config_wizard.pem_generate_hint")
  ), /* @__PURE__ */ React.createElement(Input, { id: "sp-generate-credential", label: " ", labelClassName: "col-sm-3", wrapperClassName: "col-sm-9" }, /* @__PURE__ */ React.createElement(GenerateKeyPairButton, { values, onClick: handleSpCredentialFetch(setFieldValue) })), /* @__PURE__ */ React.createElement(
    BackendFetchError,
    {
      title: t("saml.saml_backend_config_wizard.generate_sp_credentials_error_title"),
      message: spCredentialsGenerateErrors
    }
  ), /* @__PURE__ */ React.createElement(FormSection, { title: t("saml.saml_backend_config_wizard.user_attribute_mapping") }, /* @__PURE__ */ React.createElement("p", null, t("saml.saml_backend_config_wizard.user_attribute_mapping_info"))), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpUserNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("saml.saml_backend_config_wizard.attribute_names.user_name"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: t("saml.saml_backend_config_wizard.user_name_placeholder"),
      help: t("saml.saml_backend_config_wizard.user_name_help"),
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpEmailAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("saml.saml_backend_config_wizard.attribute_names.email"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: t("saml.saml_backend_config_wizard.email_placeholder"),
      help: t("saml.saml_backend_config_wizard.email_help"),
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpFirstNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("saml.saml_backend_config_wizard.attribute_names.first_name"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: t("saml.saml_backend_config_wizard.first_name_placeholder"),
      help: t("saml.saml_backend_config_wizard.first_name_help"),
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpLastNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("saml.saml_backend_config_wizard.attribute_names.last_name"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: t("saml.saml_backend_config_wizard.last_name_placeholder"),
      help: t("saml.saml_backend_config_wizard.last_name_help"),
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpFullNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("saml.saml_backend_config_wizard.attribute_names.full_name"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: t("saml.saml_backend_config_wizard.full_name_placeholder"),
      help: t("saml.saml_backend_config_wizard.full_name_help"),
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(FormSection, { title: t("saml.saml_backend_config_wizard.defaults_configuration") }), /* @__PURE__ */ React.createElement(Field, { name: "defaultRoles", validate: validateField({ required: true }) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      bsStyle: error ? "error" : void 0,
      help: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "saml.saml_backend_config_wizard.default_roles_help",
          values: { readerRole: READER_ROLE_NAME },
          components: { code: /* @__PURE__ */ React.createElement("code", null) }
        }
      )),
      error,
      id: "default-roles-select",
      label: t("saml.saml_backend_config_wizard.default_roles"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        multi: true,
        onBlur,
        onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
        options: rolesOptions,
        placeholder: t("saml.saml_backend_config_wizard.search_for_roles"),
        value
      }
    )
  )), /* @__PURE__ */ React.createElement(Field, { name: "defaultUserTimezone" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "default-user-timezone-select",
      label: t("saml.saml_backend_config_wizard.default_user_time_zone"),
      help: t("saml.saml_backend_config_wizard.default_user_time_zone_help"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        value: value || "Browser's time zone",
        name: "timezone",
        onChange: (newValue) => onChange({ target: { name, value: newValue } })
      }
    )
  )), validationErrors, submitErrors, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_IDP_CONFIG_SAVE_CLICKED, {
          app_action_value: "save"
        });
      },
      type: "submit"
    },
    t("saml.saml_backend_config_wizard.finish_and_save")
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_NEXT_TEAM_SYNC_CLICKED, {
          app_action_value: "next-step"
        });
        setActiveStep("team_sync");
      },
      type: "button"
    },
    t("saml.saml_backend_config_wizard.next_team_sync")
  )))), /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, t("saml.saml_backend_config_wizard.sp_info_text")), /* @__PURE__ */ React.createElement(StyledDefList, null, /* @__PURE__ */ React.createElement("dt", null, t("saml.saml_backend_config_wizard.sp_entity_id_label")), /* @__PURE__ */ React.createElement("dd", null, values.spEntityId ? /* @__PURE__ */ React.createElement("code", null, values.spEntityId) : /* @__PURE__ */ React.createElement("em", null, t("saml.saml_backend_config_wizard.please_add_sp_entity_id"))), /* @__PURE__ */ React.createElement("dt", null, t("saml.saml_backend_config_wizard.sp_acs_url_label")), /* @__PURE__ */ React.createElement("dd", null, values.spBaseUrl ? /* @__PURE__ */ React.createElement("code", null, normalizeURI(`${values.spBaseUrl}/${SSO_SAML_ACS_PATH}`)) : /* @__PURE__ */ React.createElement("em", null, t("saml.saml_backend_config_wizard.please_add_sp_base_url"))), /* @__PURE__ */ React.createElement("dt", null, t("saml.saml_backend_config_wizard.sp_logout_url_label")), /* @__PURE__ */ React.createElement("dd", null, values.spBaseUrl ? /* @__PURE__ */ React.createElement("code", null, normalizeURI(`${values.spBaseUrl}/${SSO_SAML_LOGOUT_PATH}`)) : /* @__PURE__ */ React.createElement("em", null, t("saml.saml_backend_config_wizard.please_add_sp_base_url"))), /* @__PURE__ */ React.createElement("dt", null, t("saml.saml_backend_config_wizard.sp_certificate_label")), /* @__PURE__ */ React.createElement("dd", null, values.spCredentialCertificateInfo && /* @__PURE__ */ React.createElement(CertificateInfo, { certInfo: values.spCredentialCertificateInfo }) || /* @__PURE__ */ React.createElement("em", null, t("saml.saml_backend_config_wizard.please_generate_sp_credentials")))), /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement("h3", null, t("saml.saml_backend_config_wizard.default_attribute_mappings")), Object.keys(defaultAttributeMapping).length === 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, t("saml.saml_backend_config_wizard.could_not_load_attribute_mappings")) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("saml.saml_backend_config_wizard.default_attribute_mappings_info")), /* @__PURE__ */ React.createElement(
    AttributeMapping,
    {
      title: t("saml.saml_backend_config_wizard.attribute_names.user_name"),
      mappings: defaultAttributeMapping.user_name
    }
  ), /* @__PURE__ */ React.createElement(
    AttributeMapping,
    {
      title: t("saml.saml_backend_config_wizard.attribute_names.email"),
      mappings: defaultAttributeMapping.email
    }
  ), /* @__PURE__ */ React.createElement(
    AttributeMapping,
    {
      title: t("saml.saml_backend_config_wizard.attribute_names.first_name"),
      mappings: defaultAttributeMapping.first_name
    }
  ), /* @__PURE__ */ React.createElement(
    AttributeMapping,
    {
      title: t("saml.saml_backend_config_wizard.attribute_names.last_name"),
      mappings: defaultAttributeMapping.last_name
    }
  ), /* @__PURE__ */ React.createElement(
    AttributeMapping,
    {
      title: t("saml.saml_backend_config_wizard.attribute_names.full_name"),
      mappings: defaultAttributeMapping.full_name
    }
  )))));
};
const TeamSyncForm = ({ isSubmitting, defaultAttributeMapping, setActiveStep, licenseIsValid }) => {
  var _a;
  const { t } = useTranslation("enterprise_authentication");
  const [{ value: isTeamSyncEnabled }] = useField("teamSyncEnabled");
  const sendTelemetry = useSendTelemetry("saml-team-sync-step");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, !licenseIsValid && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, t("saml.saml_backend_config_wizard.team_sync_license_warning")), /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
    Input,
    {
      label: t("saml.saml_backend_config_wizard.enable_team_sync"),
      id: "team-sync-checkbox",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        label: "",
        type: "checkbox",
        wrapperClassName: "col-sm-12",
        name: "teamSyncEnabled",
        help: t("saml.saml_backend_config_wizard.team_sync_enable_help"),
        disabled: isSubmitting || !licenseIsValid
      }
    )
  ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("p", null, t("saml.saml_backend_config_wizard.team_sync_info"))))), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "teamSyncGroupAttributeName",
      label: t("saml.saml_backend_config_wizard.group_attribute_name"),
      placeholder: t("saml.saml_backend_config_wizard.group_attribute_name"),
      help: t("saml.saml_backend_config_wizard.group_attribute_name_help"),
      disabled: isSubmitting || !licenseIsValid || !isTeamSyncEnabled
    }
  ), /* @__PURE__ */ React.createElement(Field, { name: "teamSyncAllowedGroups" }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      bsStyle: error ? "error" : void 0,
      help: /* @__PURE__ */ React.createElement("span", null, t("saml.saml_backend_config_wizard.allowed_groups_help")),
      error,
      id: "allowed-groups",
      label: t("saml.saml_backend_config_wizard.allowed_groups"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      MultiSelect,
      {
        id: "allowed-groups-select",
        value,
        formatCreateLabel: (label) => t("saml.saml_backend_config_wizard.add_group", { label }),
        onBlur,
        placeholder: t("saml.saml_backend_config_wizard.type_group_name"),
        options: [],
        onChange: (selectedGroups) => onChange({ target: { value: selectedGroups, name } }),
        disabled: isSubmitting || !licenseIsValid || !isTeamSyncEnabled,
        allowCreate: true
      }
    )
  )), /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_PREVIOUS_IDP_CONFIG_CLICKED, {
          app_action_value: "previous-step"
        });
        setActiveStep("idp_config");
      },
      type: "button"
    },
    t("saml.saml_backend_config_wizard.previous_idp_config")
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_TEAM_SYNC_SAVE_CLICKED, {
          app_action_value: "save"
        });
      },
      type: "submit"
    },
    t("saml.saml_backend_config_wizard.finish_and_save")
  )))), /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("h3", null, t("saml.saml_backend_config_wizard.default_group_attribute_mappings")), Object.keys(defaultAttributeMapping).length === 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, t("saml.saml_backend_config_wizard.could_not_load_group_attribute_mappings")) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("saml.saml_backend_config_wizard.group_attribute_mappings_info")), /* @__PURE__ */ React.createElement("ul", null, ((_a = defaultAttributeMapping.groups) != null ? _a : []).sort().map((attributeName) => /* @__PURE__ */ React.createElement("li", { key: attributeName }, /* @__PURE__ */ React.createElement("code", null, attributeName))))))));
};
const defaultValues = {
  type: AUTH_SERVICE_TYPE,
  title: "",
  description: "",
  defaultRoles: "",
  defaultUserTimezone: void 0,
  idpMetadataUrl: "",
  idpMetadataRefreshAtLogin: false,
  idpSsoServiceRedirectBindingUrl: "",
  idpSsoServicePostBindingUrl: "",
  idpLogoutServiceRedirectBindingUrl: void 0,
  idpLogoutServicePostBindingUrl: void 0,
  idpEntityId: "",
  idpCertificate: "",
  idpCertificateFilename: "",
  idpCertificateInfo: void 0,
  idpCertificateInfoError: void 0,
  idpUserNameAttribute: void 0,
  idpEmailAttribute: void 0,
  idpFirstNameAttribute: void 0,
  idpLastNameAttribute: void 0,
  idpFullNameAttribute: void 0,
  spBaseUrl: DEFAULT_SP_BASE_URL,
  spEntityId: normalizeURI(`${DEFAULT_SP_BASE_URL}${SSO_SAML_PATH}`),
  spCredentialCertificate: "",
  spCredentialCertificateFilename: "",
  spCredentialCertificateInfo: void 0,
  spCredentialCertificateInfoError: void 0,
  spCredentialPrivateKey: { is_set: false },
  spCredentialPrivateKeyFilename: "",
  teamSyncIsCurrentlyEnabled: false,
  teamSyncEnabled: false,
  teamSyncGroupAttributeName: void 0,
  teamSyncAllowedGroups: void 0
};
const SAMLBackendConfigWizard = ({
  authBackend = defaultValues,
  initialStep = "idp_config",
  licenseIsValid
}) => {
  var _a;
  const { t } = useTranslation("enterprise_authentication");
  const [activeStep, setActiveStep] = useState(initialStep);
  const [submitErrors, setSubmitErrors] = useState(void 0);
  const [metadataFetchErrors, setMetadataFetchErrors] = useState(void 0);
  const [spCredentialsGenerateErrors, setSpCredentialsGenerateErrors] = useState(void 0);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const { isLoading, roles } = useRoles();
  const history = useHistory();
  const { isLoading: isDefaultAttributeMappingLoading, defaultAttributeMapping } = useSAMLDefaultAttributeMapping();
  if (isLoading || isDefaultAttributeMappingLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const onFormSubmit = (values, { setErrors, setSubmitting }) => {
    samlAuthBackendSubmit(values).then(() => {
      history.push(Routes.SYSTEM.AUTHENTICATION.BACKENDS.OVERVIEW);
    }).catch((error) => {
      var _a2, _b;
      if (typeof ((_b = (_a2 = error == null ? void 0 : error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.errors) === "object") {
        setErrors(
          formatAuthBackendValidationErrors(error.additional.body.errors, (v) => ({
            // Add certificate errors to custom form field names to show them in the UI
            idpCertificateFilename: v.idpCertificates,
            spCredentialCertificateFilename: v.spCredentials,
            ...v
          }))
        );
      } else {
        setSubmitErrors(error);
      }
    }).finally(() => setSubmitting(false));
  };
  const handleMetadataFetch = (values, setFieldValue) => () => {
    setIsFetchingMetadata(true);
    SAMLConfig.getIdpMetadata({ url: values.idpMetadataUrl }).then((idpMetadata) => {
      var _a2;
      setFieldValue("idpSsoServiceRedirectBindingUrl", idpMetadata.sso_service_redirect_binding_url);
      setFieldValue("idpSsoServicePostBindingUrl", idpMetadata.sso_service_post_binding_url);
      setFieldValue("idpLogoutServiceRedirectBindingUrl", idpMetadata.logout_service_redirect_binding_url);
      setFieldValue("idpLogoutServicePostBindingUrl", idpMetadata.logout_service_post_binding_url);
      setFieldValue("idpEntityId", idpMetadata.entity_id);
      setFieldValue("idpCertificate", (_a2 = idpMetadata.certificates) == null ? void 0 : _a2[0]);
      setFieldValue("idpCertificateFilename", idpMetadata.certificates && "cert-from-metadata.pem");
      setMetadataFetchErrors(void 0);
    }).catch((error) => {
      var _a2, _b;
      if ((_b = (_a2 = error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.message) {
        setMetadataFetchErrors(error.additional.body.message);
      } else {
        setMetadataFetchErrors(error.toString());
      }
    }).finally(() => setIsFetchingMetadata(false));
  };
  const handleSpCredentialFetch = (setFieldValue) => () => {
    SAMLConfig.generateSpCredentials().then((credentialResponse) => {
      setFieldValue("spCredentialCertificate", credentialResponse.certificate);
      setFieldValue("spCredentialCertificateFilename", "generated-sp-certificate.pem");
      setFieldValue("spCredentialPrivateKey", { set_value: credentialResponse.private_key });
      setFieldValue("spCredentialPrivateKeyFilename", "generated-sp-key.pem");
      setSpCredentialsGenerateErrors(void 0);
    }).catch((error) => {
      var _a2, _b;
      if ((_b = (_a2 = error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.message) {
        setSpCredentialsGenerateErrors(error.additional.body.message);
      } else {
        setSpCredentialsGenerateErrors(error.toString());
      }
    });
  };
  const submitErrorComponent = submitErrors && /* @__PURE__ */ React.createElement(AuthBackendSubmitError, { error: submitErrors, backendId: authBackend.id });
  const defaultCreateRoleId = (_a = roles.find((role) => role.name === READER_ROLE_NAME)) == null ? void 0 : _a.id;
  const defaultRolesValues = authBackend.defaultRoles;
  const initialValues = {
    ...authBackend,
    defaultRoles: defaultRolesValues === "" ? defaultCreateRoleId : defaultRolesValues,
    defaultUserTimezone: authBackend.defaultUserTimezone
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues,
      enableReinitialize: true,
      onSubmit: onFormSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount: false
    },
    ({ isSubmitting, values, setFieldValue, errors }) => /* @__PURE__ */ React.createElement(
      Wizard,
      {
        hidePreviousNextButtons: true,
        justified: true,
        horizontal: true,
        containerClassName: "",
        onStepChange: setActiveStep,
        activeStep,
        steps: [
          {
            key: "idp_config",
            title: /* @__PURE__ */ React.createElement("span", null, t("saml.saml_backend_config_wizard.idp_configuration")),
            component: /* @__PURE__ */ React.createElement(
              IdPConfigForm,
              {
                values,
                roles,
                isSubmitting,
                isFetchingMetadata,
                validationErrors: /* @__PURE__ */ React.createElement(ValidationErrorSummary, { errors }),
                submitErrors: submitErrorComponent,
                metadataFetchErrors,
                spCredentialsGenerateErrors,
                setActiveStep,
                setFieldValue,
                handleMetadataFetch,
                handleSpCredentialFetch,
                defaultAttributeMapping
              }
            )
          },
          {
            key: "team_sync",
            title: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
              Trans,
              {
                t,
                i18nKey: "saml.saml_backend_config_wizard.team_sync_title",
                components: { small: /* @__PURE__ */ React.createElement("small", null) }
              }
            )),
            component: /* @__PURE__ */ React.createElement(
              TeamSyncForm,
              {
                isSubmitting,
                defaultAttributeMapping,
                setActiveStep,
                licenseIsValid
              }
            )
          }
        ]
      }
    )
  );
};
export default SAMLBackendConfigWizard;
